import { useCallback } from "react";
import { useCreateSource } from "../../features/deploy-knowledge/knowledge/use-knowledge";
import { useErrorMessage } from "../../lib/api-errors";
import type { QaDraft } from "./qa-model";
import type { SourceTargets } from "./use-source-targets";

export interface QaSaveOutcome {
  /** All rows, with `sourceId` set on the ones that were saved. */
  drafts: QaDraft[];
  saved: number;
  /** Rows that could not be saved, and the first reason (user-safe API message). */
  failed: QaDraft[];
  reason: string | null;
}

/** Saves each complete pair as its own Q&A source (one request each, so one failure keeps the rest). */
export function useQaSave(targets: SourceTargets) {
  const { mutateAsync } = useCreateSource();
  const message = useErrorMessage();
  return useCallback(
    async (drafts: readonly QaDraft[], pending: readonly QaDraft[]): Promise<QaSaveOutcome> => {
      const sourceIds = new Map<string, string>();
      const failed: QaDraft[] = [];
      let reason: string | null = null;
      for (const draft of pending) {
        try {
          const created = await mutateAsync({ type: "qa", question: draft.question.trim(), answer: draft.answer.trim(), audiences: [...targets.audiences], agentIds: targets.agentIds });
          sourceIds.set(draft.id, created.source_id);
        } catch (caught) {
          failed.push(draft);
          reason ??= message(caught);
        }
      }
      const saved = drafts.map((draft) => (sourceIds.has(draft.id) ? { ...draft, sourceId: sourceIds.get(draft.id) } : draft));
      return { drafts: saved, saved: sourceIds.size, failed, reason };
    },
    [mutateAsync, targets, message],
  );
}
